import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

export default function NewsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const API = process.env.PARCEL_API_URL;

  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewerOpen, setViewerOpen] = useState(false);

  // 🔥 FETCH DATA
  useEffect(() => {
    if (!API) return;

    axios
      .get(`${API}/api/news/${id}`)
      .then((res) => setNews(res.data))
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  }, [id, API]);

  // 🔥 LOCK SCROLL
  useEffect(() => {
    document.body.style.overflow = viewerOpen ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [viewerOpen]);

  // 🔥 ESC CLOSE
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && setViewerOpen(false);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  // 🔥 SKELETON
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 px-6 md:px-20 py-16 animate-pulse">
        <div className="h-4 w-32 bg-gray-300 rounded mb-6"></div>
        <div className="h-10 w-2/3 bg-gray-300 rounded mb-8"></div>
        <div className="w-full h-80 bg-gray-300 rounded-2xl mb-10"></div>

        <div className="space-y-3 max-w-3xl">
          <div className="h-4 bg-gray-300 rounded"></div>
          <div className="h-4 bg-gray-300 rounded w-5/6"></div>
          <div className="h-4 bg-gray-300 rounded w-4/6"></div>
        </div>
      </div>
    );
  }

  if (!news) {
    return <p className="p-10 text-center">News not found</p>;
  }

  return (
    <>
      {/* 🔥 FULLSCREEN IMAGE VIEWER */}
      <AnimatePresence>
        {viewerOpen && (
          <motion.div
            onClick={() => setViewerOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md"
          >
            {/* CLOSE */}
            <button
              onClick={() => setViewerOpen(false)}
              className="absolute top-6 right-6 text-white text-3xl bg-white/10 px-3 py-1 rounded-full hover:bg-white/20 transition"
            >
              ✕
            </button>

            {/* IMAGE */}
            <motion.img
              src={news.image}
              alt={news.title}
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="max-h-[90vh] max-w-[95vw] object-contain rounded-xl shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🔥 MAIN PAGE */}
      <div className="min-h-screen bg-gray-50">

        {/* TOP BAR */}
        <div className="px-6 md:px-20 pt-10">
          <button
            onClick={() => navigate("/news")}
            className="text-orange-500 font-medium hover:underline"
          >
            ← Back to News
          </button>
        </div>

        {/* CONTENT */}
        <div className="max-w-4xl mx-auto px-6 md:px-0 py-10">

          {/* 🔥 HEADING */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-3xl md:text-5xl font-extrabold leading-tight mb-4 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-orange-400 to-amber-500"
          >
            {news.title}
          </motion.h1>

          {/* 🔥 ACCENT LINE */}
          <div className="w-20 h-1 bg-gradient-to-r from-orange-500 to-amber-400 rounded mb-8"></div>

          {/* ✅ HERO IMAGE FIXED */}
          {news.image && (
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="overflow-hidden rounded-2xl shadow-lg mb-10 cursor-pointer bg-black flex items-center justify-center"
              onClick={() => setViewerOpen(true)}
            >
              <img
                src={news.image}
                alt={news.title}
                className="max-h-[450px] w-full object-contain"
              />
            </motion.div>
          )}

          {/* CONTENT CARD */}
          <div className="bg-white rounded-2xl shadow-md p-6 md:p-10">
            <p className="text-gray-700 text-lg leading-relaxed tracking-wide">
              {news.desc}
            </p>
          </div>

        </div>
      </div>
    </>
  );
}